import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { translations } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopContext = createContext(null);

export const useShop = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
  const [lang, setLang] = useState("tr");
  const [products, setProducts] = useState(null);
  const [options, setOptions] = useState(null);
  const [content, setContent] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vlp-cart")) || [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  const refreshProducts = () =>
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));

  const refreshOptions = () =>
    axios
      .get(`${API}/options`)
      .then((r) => setOptions(r.data))
      .catch(() => setOptions({ series: [], dims: [] }));

  const refreshContent = () =>
    axios
      .get(`${API}/content`)
      .then((r) => setContent(r.data))
      .catch(() => setContent({ fields: {} }));

  const refreshContacts = () =>
    axios
      .get(`${API}/contact`)
      .then((r) => setContacts(r.data || []))
      .catch(() => setContacts([]));

  useEffect(() => {
    refreshProducts();
    refreshOptions();
    refreshContent();
    refreshContacts();
  }, []);

  useEffect(() => {
    localStorage.setItem("vlp-cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title =
      lang === "tr"
        ? "VLP Dolap | VOLPAR — Kombi Dolabı & PVC Dolap Sistemleri"
        : "VLP Dolap | VOLPAR — Premium PVC Boiler Cabinets";
  }, [lang]);

  const t = translations[lang];

  const productMedia = (p) =>
    p?.image_path ? `${API}/products/${p.id}/media?type=photo` : p?.image_url;
  const productVideo = (p) =>
    p?.video_path ? `${API}/products/${p.id}/media?type=video` : null;
  const seriesLabel = (key) => {
    const o = options?.series?.find((s) => s.key === key);
    return o ? (lang === "tr" ? o.label_tr : o.label_en) : key;
  };
  const cval = (key, fallback) => content?.fields?.[key] || fallback;
  const contentImage = (section) =>
    section === "hero" && content?.hero_image_path
      ? `${API}/content/image/hero`
      : section === "story" && content?.story_image_path
        ? `${API}/content/image/story`
        : null;

  const addToCart = (id, dims = null) =>
    setCart((prev) => {
      const found = prev.find((i) => i.id === id && i.dims === dims);
      return found
        ? prev.map((i) => (i.id === id && i.dims === dims ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { id, dims, qty: 1 }];
    });

  const updateQty = (id, dims, delta) =>
    setCart((prev) =>
      prev
        .map((i) => (i.id === id && i.dims === dims ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );

  const removeFromCart = (id, dims) =>
    setCart((prev) => prev.filter((i) => !(i.id === id && i.dims === dims)));

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <ShopContext.Provider
      value={{
        lang,
        setLang,
        t,
        products,
        refreshProducts,
        productMedia,
        productVideo,
        options,
        refreshOptions,
        seriesLabel,
        content,
        refreshContent,
        cval,
        contentImage,
        contacts,
        refreshContacts,
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};
